import axios, { type AxiosError, type AxiosInstance } from 'axios';
import qs from 'qs';

import { SITE_COMPLIANCE_API_URL } from '@/config/env';
import { siteComplianceRequest } from '@/config/msalConfig';

import { getToken, triggerReauthentication } from './utils';

export interface SiteComplianceProblemDetails {
  code?: string;
  detail?: string | null;
  error?: string | { code?: string; message?: string };
  instance?: string | null;
  status?: number | null;
  title?: string | null;
  type?: string | null;
  [key: string]: unknown;
}

export type SiteComplianceApiError = AxiosError<SiteComplianceProblemDetails> & {
  code?: string;
  correlationId?: string;
  durationMs?: number;
};

const attachSiteComplianceAuthInterceptors = (api: AxiosInstance) => {
  api.interceptors.request.use(async (config) => {
    config.headers = config.headers ?? {};

    config.headers.Authorization = `Bearer ${await getToken(
      siteComplianceRequest,
    )}`;
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error: SiteComplianceApiError) => {
      if (error?.response?.status === 401) {
        try {
          await triggerReauthentication(siteComplianceRequest);
        } catch (reauthenticationError) {
          console.error(reauthenticationError);
        }
      }

      if (
        error?.response?.status === 401 ||
        error?.response?.status === 403 ||
        error?.response?.status === 404 ||
        (error?.response?.status ?? 0) >= 500
      ) {
        const responseData = error.response?.data;
        const nestedError =
          responseData?.error && typeof responseData.error === 'object'
            ? responseData.error
            : null;
        const responseCode = responseData?.code ?? nestedError?.code;

        if (responseCode) {
          error.code = responseCode;
        }
      }

      return Promise.reject(error);
    },
  );
};

export const siteComplianceApi = axios.create({
  baseURL: SITE_COMPLIANCE_API_URL,
  // `encode: true` (default) is required so values that contain reserved
  // characters (notably `+` inside Cosmos continuation tokens) are properly
  // URL-encoded — otherwise the backend decodes `+` as a space and 500s.
  paramsSerializer: (params) =>
    qs.stringify(params, { arrayFormat: 'repeat' }),
});

attachSiteComplianceAuthInterceptors(siteComplianceApi);
